/**
 * PostMinimal + M3 AiOutputFormat → ECW 分析用フィールドマップ への変換アダプタ
 * (Sprint BG-DataSettings 18、GAP U-L8 / U-L12 / U-L18 close)。
 *
 * 旧 `old/apps/user/src/lib/ecwAdapter.ts` の核 (`extractAiAxesFields` / `applyOutputFormatToCustomFields`)
 * を踏襲。旧の `postDocToEcwPostData` 全体は v2 で不要 (PostMinimal が既に分解済) のため、
 * 必要な抽出関数のみを移植。
 *
 * 踏襲点:
 *   - M3.classificationAxes に従って AI 解析結果から `{axis.key}` / `{axis.key}Code` /
 *     `{axis.key}Name` / `{axis.key}Codes` / `{axis.key}Names` / `{axis.key}MajorCodes` を抽出
 *   - `majorCodeExtract` ('before-hyphen' / 'first-char') による大分類抽出
 *   - snake_case / camelCase 両対応 (`readFieldArray` 二重 lookup)
 *   - aggregateScores.positiveKey / negativeKey から positiveScore / negativeScore / assessmentScore 計算
 *   - mode 別 (ecw / bousai / walker) の AI 結果 root key 自動判別
 *
 * 変更点 (v2):
 *   - 入力を `PostDoc` (旧 lib/posts.ts) → `PostMinimal` (v2 features/posts/usePosts.ts) に変更
 *   - 旧の `EcwPostData` 全体生成は廃止、`Record<string, string | number>` ベースの「軸抽出マップ」のみ返却
 *   - top-level プロパティ書込 (`base.scope = ...`) は v2 では不要 (PostDetailModal が PostMinimal を直接読む)
 *   - matrix view 用 helper `extractEcwAxisCodes` を追加 (v2 AnalysisMatrixView が hardcode していた
 *     `aiAnalysisEcw.emotions` / `aiAnalysisEcw.categoryCodes` 直参照を M3 駆動に切替)
 *
 * 廃止点:
 *   - `resolveCategoryValue` / `resolveSubTypeValue` (bousai 専用、v2 では AnalysisMatrixView 範囲外)
 *   - `formatDate` / `formatTime` 等 EcwPostData 構築用ヘルパー (v2 では未使用)
 *   - top-level `EcwPostData` 互換 layer (Phase 3 で旧 v1 が既に customFields 一本化済、v2 で完全廃止)
 *
 * 関連: docs/91 U-L8 (PostMinimal → ECW 形式変換)、U-L18 (analysis hardcode 削除)。
 */
import {
  axisValueField,
  normalizeAxisRoot,
  readAxisCodes,
  readResultField,
  resolveFormatAxes,
  splitCodeValue,
  type AiOutputFormat,
  type AiOutputFormatSchema,
  type ResolvedFormatAxis,
} from '@citizen-walk/shared-types';
import type { AiCategoryMastersResult, PostMinimal } from '../posts';

/**
 * cls / projected 擬似 post で分類軸の code/name/major を extraFields に mirror する際の予約 prefix。
 *
 * 分類軸の key (M3 `classificationAxes[].key`、例 'category') は、地区の投稿フィールド key
 * (`dataSettings.postFields[].key`、例 'category'=タイプ) と一致しうる。同一 namespace に書くと
 * mirror が投稿フィールド値を上書きし、`postField:<key>` 軸が壊れる (タイプ軸が空になる回帰)。
 * 分類軸 mirror をこの予約 prefix 付き key に隔離することで、どのモード・どの軸設定でも
 * 投稿フィールドと分類軸が衝突しないようにする。`__clsAxis:` は投稿フィールド key になり得ない
 * (識別子に `:` を含まない) ため安全。書込は `mirrorRowToExtraFields`、読出は本ファイルの
 * generic fallback (下記) のみ。
 */
export const CLS_AXIS_MIRROR_PREFIX = '__clsAxis:';

function str(val: unknown): string {
  if (val == null) return '';
  return String(val);
}

function num(val: unknown, fallback = 0): number {
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
}

/** "category_codes" → "categoryCodes" (旧踏襲)。 */
function snakeToCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

/** 分類コードから大分類部分を抽出 (旧踏襲、majorCodeExtract 駆動)。 */
function extractMajorCode(code: string, mode: 'first-char' | 'before-hyphen' | undefined): string {
  if (!code) return '';
  if (mode === 'first-char') return code.charAt(0);
  // 'before-hyphen' or default
  const idx = code.indexOf('-');
  return idx > 0 ? code.substring(0, idx) : code;
}

/**
 * result から fieldKey に対応する値を string[] として取り出す (旧 readFieldArray 踏襲)。
 * snake_case / camelCase 両対応、配列 / 単一値 / number を string[] に正規化。
 */
function readFieldArray(result: Record<string, unknown>, fieldKey: string | undefined): string[] {
  if (!fieldKey) return [];
  const variants = fieldKey.includes('_') ? [fieldKey, snakeToCamel(fieldKey)] : [fieldKey];
  for (const k of variants) {
    const v = result[k];
    if (Array.isArray(v) && v.length > 0) {
      return v.filter((x) => x != null && x !== '').map(String);
    }
    if (typeof v === 'string' && v.length > 0) return [v];
    if (typeof v === 'number') return [String(v)];
  }
  return [];
}

/**
 * PostMinimal の AI 解析結果から mode に応じた result root を取得。
 * v2 では aiAnalysisBousai / aiAnalysisWalker / aiAnalysisEcw の 3 経路で公開済。
 *
 * custom mode (例 keikan) は mode 別 result を持たず `aiAnalysisGeneric` (fields[] 駆動の
 * 汎用結果) のみを持つ。組込モードは上の 3 経路が優先されるため、generic 分岐に来るのは
 * 「generic しか持たない custom mode」のときだけ — 組込モードの挙動は不変 (加法的追加)。
 */
function getModeResult(post: PostMinimal): Record<string, unknown> | null {
  if (post.aiAnalysisBousai) return post.aiAnalysisBousai as unknown as Record<string, unknown>;
  if (post.aiAnalysisWalker) return post.aiAnalysisWalker as unknown as Record<string, unknown>;
  if (post.aiAnalysisEcw) return post.aiAnalysisEcw as unknown as Record<string, unknown>;
  if (post.aiAnalysisGeneric) return post.aiAnalysisGeneric as unknown as Record<string, unknown>;
  // fallback: rawOutput.parsed (旧踏襲)
  const raw = post.aiAnalysisRaw;
  if (raw && raw.parsed && typeof raw.parsed === 'object') {
    return raw.parsed as Record<string, unknown>;
  }
  return null;
}

/**
 * AiOutputFormat.schema を元に AI 解析結果から軸フィールドマップを生成する純粋関数。
 * 旧 `extractAiAxesFields` を PostMinimal 入力に対応させた踏襲版。
 *
 * 生成キー (旧と同一、key は M3.classificationAxes[].key):
 *   - {key}, {key}Code, {key}Name, {key}Codes, {key}Names, {key}MajorCodes, {key}Sub
 *   - positiveScore / negativeScore / assessment / assessmentScore
 */
export function extractAiAxesFields(
  post: PostMinimal,
  format: AiOutputFormat | null | undefined,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (!format) return out;
  if (post.aiAnalysisStatus !== 'done') return out;

  const modeResult = getModeResult(post);
  if (!modeResult) return out;

  const schema: AiOutputFormatSchema = format.schema;

  let result: Record<string, unknown> = modeResult;
  if (schema.rootKey) {
    const nested = modeResult[schema.rootKey];
    if (nested && typeof nested === 'object') {
      result = nested as Record<string, unknown>;
    }
  }

  const classifications =
    (result.classifications as Array<Record<string, unknown>> | undefined) ?? [];
  const top = classifications[0] ?? null;

  for (const axisDef of schema.classificationAxes) {
    let codes: string[] = [];
    let names: string[] = [];
    let sub: string[] = [];
    if (top) {
      const code = str(top[axisDef.codeField] ?? '');
      const name = str(top[axisDef.nameField] ?? '');
      if (code) codes = [code];
      if (name) names = [name];
      const rawSub = axisDef.subField ? top[axisDef.subField] : undefined;
      if (Array.isArray(rawSub)) sub = rawSub.map(String);
    }
    // 分類の配列があっても、**その行に無い軸**は解析結果の直下に置かれていることがある
    // (ECW＋ウォーカブル: classifications[] はゲール基準だけを持ち、対象物カテゴリと感情は
    // 直下の category_codes / emotions にある)。行から取れなかった軸はフラット構造を読む。
    // 2026-09-07 まではここで行しか見ておらず、直下に置かれた軸が丸ごと欠落していた。
    if (codes.length === 0 && names.length === 0) {
      // コードは "F-02,A-02,B-09" のようにカンマ区切り 1 本の文字列で来ることがある。
      // 大分類の抽出はコードを 1 件ずつ見る必要があるので、コード側だけ区切って配列化する
      // (名前は本文にカンマを含みうるので分割しない)。
      codes = readFieldArray(result, axisDef.codeField).flatMap((c) => splitCodeValue(c));
      names = readFieldArray(result, axisDef.nameField);
    }
    const codeFirst = codes[0] ?? '';
    const nameFirst = names[0] ?? '';
    const key = (axisDef as { key?: string }).key ?? axisDef.codeField;
    out[key] = nameFirst || codeFirst;
    out[key + 'Code'] = codeFirst;
    out[key + 'Name'] = nameFirst;
    if (codes.length > 0) out[key + 'Codes'] = codes.join(',');
    if (names.length > 0) out[key + 'Names'] = names.join(',');
    if (codes.length > 0) {
      const majorCodes = [
        ...new Set(
          codes
            .map((c) =>
              extractMajorCode(
                c,
                (axisDef as { majorCodeExtract?: 'first-char' | 'before-hyphen' }).majorCodeExtract,
              ),
            )
            .filter((s) => s.length > 0),
        ),
      ];
      if (majorCodes.length > 0) out[key + 'MajorCodes'] = majorCodes.join(',');
    }
    if (sub.length > 0) out[key + 'Sub'] = sub.join(',');
  }

  const agg = schema.aggregateScores;
  if (agg?.positiveKey) {
    const v = result[agg.positiveKey];
    const avg =
      typeof v === 'number'
        ? v
        : v && typeof v === 'object'
          ? Number((v as { avg?: unknown }).avg ?? 0)
          : 0;
    if (Number.isFinite(avg)) out.positiveScore = Math.round(avg * 10) / 10;
  }
  if (agg?.negativeKey) {
    const v = result[agg.negativeKey];
    const avg =
      typeof v === 'number'
        ? v
        : v && typeof v === 'object'
          ? Number((v as { avg?: unknown }).avg ?? 0)
          : 0;
    if (Number.isFinite(avg)) out.negativeScore = Math.round(avg * 10) / 10;
  }

  if (top?.assessment) {
    out.assessment = str(top.assessment);
  } else if (agg?.positiveKey && agg?.negativeKey) {
    const pos = num(out.positiveScore);
    const neg = num(out.negativeScore);
    if (pos !== 0 || neg !== 0) {
      out.assessment =
        pos >= neg ? schema.assessment.positiveLabel : schema.assessment.negativeLabel;
    }
  }

  const pos = num(out.positiveScore);
  const neg = num(out.negativeScore);
  if (pos !== 0 || neg !== 0) {
    out.assessmentScore = Math.max(-3, Math.min(3, Math.round(pos - neg)));
  }

  return out;
}

/**
 * M3 駆動 (モード非依存) で、1 軸のコード集合を解析結果から集める。
 *
 * 読み取り元の優先順位:
 *   1. `classifications[]` があれば **代表 (rank 最小)** の当該軸フィールド
 *      (1 投稿が複数セルに重複表示されるのを防ぐ。全分類を個別に見るのは AI 認識物単位)
 *   2. 代表行に当該軸のフィールドが無ければ **フラット構造** の当該軸フィールドを全件
 *      (ECW 系の `category_codes: "C-16,C-15,B-06"` / `emotions: [...]` / `gql_code: "GQL-05"`)
 *   3. cls 擬似 post は extraFields にミラー済みの軸 code
 *
 * 2 の条件は 2026-09-07 まで「`classifications[]` が **無ければ**」だった。そのため
 * ECW＋ウォーカブル (classifications[] はゲール基準のみ、対象物カテゴリと感情は直下) のような
 * テンプレートでは、直下に置かれた軸が常に空になり、色分け・フィルタ・マトリクスで
 * その軸だけ何も反映されない (ヒートマップならピンが既定色のまま) 状態になっていた。
 */
function collectAxisCodes(
  post: PostMinimal,
  schema: AiOutputFormatSchema,
  resolved: ResolvedFormatAxis,
  codes: Set<string>,
): void {
  let result = getModeResult(post);
  if (result && schema.rootKey) {
    const nested = result[schema.rootKey];
    if (nested && typeof nested === 'object') result = nested as Record<string, unknown>;
  }
  if (result) {
    const cls = result.classifications;
    let foundInClassification = false;
    if (Array.isArray(cls) && cls.length > 0) {
      const rep = representativeClassification(cls);
      if (rep) {
        const field = axisValueField(resolved.axis);
        for (const c of splitCodeValue(readResultField(rep, field), resolved.separator)) {
          codes.add(c);
          foundInClassification = true;
        }
      }
    }
    // 代表行から取れなかった軸は解析結果の直下を読む (上の JSDoc 参照)。
    if (!foundInClassification) {
      for (const c of readAxisCodes(result, resolved)) codes.add(c);
    }
  }
  // cls 擬似 post: clsRowToPost が予約 prefix 付き key に row.axes の code を mirror する
  // (投稿フィールド key との衝突回避。CLS_AXIS_MIRROR_PREFIX 参照)。
  const mirrorKey = CLS_AXIS_MIRROR_PREFIX + (resolved.axis.key ?? resolved.axis.codeField);
  const mirror = post.extraFields?.[mirrorKey];
  if (typeof mirror === 'string' && mirror) codes.add(mirror);
}

/**
 * AnalysisMatrixView 用: post + axisKey から、軸グルーピング用の code 集合を取得する。
 * 旧 v2 `AnalysisMatrixView.tsx::postToCodes` の hardcode (aiAnalysisEcw.emotions /
 * aiAnalysisEcw.categoryCodes 直参照) を M3 駆動に切替えるための entry point。
 *
 * 解決順 (issue #33 モードドリブン化、2026-08-31):
 *   1. **M3 駆動 (モード非依存)**: axisKey の root (`{root}.{level}` の root) を M3 の分類軸に
 *      解決し (明示 classificationAxes → 無ければ fields[] から導出)、解析結果から code を集める。
 *      classifications[] / フラット構造 / cls ミラー のいずれでも同じ経路で解決される。
 *   2. 旧 M3 経路: extractAiAxesFields の `<axisKey>Codes` / `<axisKey>MajorCodes`
 *   3. 【廃止予定 fallback】hardcoded ECW 軸 (`emotion.*` → aiAnalysisEcw.emotions /
 *      `category.*` → aiAnalysisEcw.categoryCodes)。M3 が解決できない旧地区のみ到達する。
 */
export function extractEcwAxisCodes(
  post: PostMinimal,
  axisKey: string,
  format: AiOutputFormat | null | undefined,
  resolver?: AiCategoryMastersResult,
): Set<string> {
  const codes = new Set<string>();

  // (1) M3 駆動 (モード非依存)。明示軸が無いフォーマットでも fields[] から軸を導出する。
  if (format) {
    const root = axisKey.split('.')[0] ?? axisKey;
    const resolved = resolveFormatAxisForRoot(format, root, resolver);
    if (resolved) {
      collectAxisCodes(post, format.schema, resolved, codes);
      if (codes.size > 0) return codes;
    }
  }

  // (2) 旧 M3 経路: format に基づく軸抽出 (axisKey が M3.classificationAxes[].key と一致する場合)
  if (format) {
    const axes = extractAiAxesFields(post, format);
    // axisKey の最終トークン (例 'walker.gql' → 'gql') を試す
    const lastToken = axisKey.split('.').pop() ?? axisKey;
    const candidates = [
      axisKey + 'Codes',
      axisKey + 'MajorCodes',
      lastToken + 'Codes',
      lastToken + 'MajorCodes',
    ];
    for (const k of candidates) {
      const v = axes[k];
      if (typeof v === 'string' && v.length > 0) {
        for (const c of v
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)) {
          codes.add(c);
        }
        if (codes.size > 0) return codes;
      }
    }
  }

  // (3)【廃止予定 fallback】後方互換 (M3 未設定 / format 未指定時): v2 hardcoded ECW 軸。
  // M3 (明示軸 or fields[] 由来) で解決できる地区は (1) で return 済のためここには来ない。
  // 撤去条件: 出力フォーマット未設定 (format=null) の地区が 0 件になったとき。
  if (post.aiAnalysisEcw) {
    if (axisKey === 'emotion.major' || axisKey === 'emotion.category') {
      for (const e of post.aiAnalysisEcw.emotions) {
        if (e) codes.add(e);
      }
    } else if (axisKey === 'category.major' || axisKey === 'category.categoryCd') {
      for (const c of post.aiAnalysisEcw.categoryCodes) {
        if (c) codes.add(c);
      }
    }
    if (codes.size > 0) return codes;
  }

  return codes;
}

/**
 * classifications[] の「代表 = rank 最小の要素」を返す (投稿単位の第1位代表用)。
 * rank が数値でない / 欠落なら Infinity 扱い。同 rank は配列の先勝ち。空配列は null。
 */
export function representativeClassification(
  classifications: unknown,
): Record<string, unknown> | null {
  if (!Array.isArray(classifications)) return null;
  let best: Record<string, unknown> | null = null;
  let bestRank = Infinity;
  for (const c of classifications) {
    if (!c || typeof c !== 'object') continue;
    const rec = c as Record<string, unknown>;
    const r = Number(rec.rank);
    const rank = Number.isFinite(r) ? r : Infinity;
    if (best === null || rank < bestRank) {
      best = rec;
      bestRank = rank;
    }
  }
  return best;
}

/**
 * matrix の templateVar 由来軸 root (例 'keikanAspect' / 'category' / 'gql') を、
 * M3 の分類軸に解決する (モード非依存)。
 *   1. 軸の key が root と直接一致するもの
 *   2. resolver で `${root}Master` → master.id を引き、masterId 一致する軸
 *   3. root を正規化して軸 root と一致するもの
 *      (例 master `emotionMaster` の root `emotion` ⇄ 出力キー `emotions`、
 *       `gqlMaster` の root `gql` ⇄ 出力キー `gql_code`)
 *
 * 軸の集合は `resolveFormatAxes` が返すもの = 明示 `classificationAxes`、無ければ
 * `fields[]` からの導出。これにより「軸を明示していない出力フォーマット」(ECW 系) でも
 * モード別ハードコードなしに軸が解決される。見つからなければ null。
 */
function resolveFormatAxisForRoot(
  format: AiOutputFormat,
  root: string,
  resolver?: AiCategoryMastersResult,
): ResolvedFormatAxis | null {
  const axes = resolveFormatAxes(format.schema);
  if (axes.length === 0) return null;
  const direct = axes.find((a) => (a.axis.key ?? a.axis.codeField) === root);
  if (direct) return direct;
  if (resolver) {
    const master = resolver.byTemplateVar.get(`${root}Master`);
    if (master) {
      const byMaster = axes.find((a) => a.axis.masterId === master.id);
      if (byMaster) return byMaster;
    }
  }
  const normalized = normalizeAxisRoot(root);
  return axes.find((a) => a.root === normalized) ?? null;
}
